import { create } from 'zustand';
import type { GitPrepared, GitSaved, GitNeedsAction } from './publish-stream';

/** Where one folder's publish stands while (or after) a publish this window started; absent = idle. */
export type PublishRowState =
  | { phase: 'prepared'; prepared: GitPrepared; action?: GitNeedsAction }
  | ({ phase: 'saved' } & GitSaved)
  | ({ phase: 'needs-action' } & GitNeedsAction)
  | { phase: 'requested'; message: string }
  | { phase: 'ready'; message: string }
  | { phase: 'publishing'; message: string }
  | { phase: 'error'; message: string; detail?: string }
  /** `undo`: the publish this run made, while the review offers to undo it (FRONT-3368). */
  | { phase: 'done'; warnings: string[]; undo?: { slug: string; publishId: number } };

/**
 * FRONT-3077: the window's publishes, held outside any component. Three
 * readers that mount and unmount independently need the same answer:
 *
 * - `running` — which folders have a publish running right now. The header's
 *   app switcher, its New button and its Save disc refuse while the bound app
 *   is publishing, and "Open in Pro Builder" refuses while any is.
 * - `rows` — each folder's progress, error or warnings. The header's Save disc
 *   shows the progress message as its hint while it runs, and the publish
 *   dialog (the details view) shows the same row. Kept here, not in component
 *   state, because the header remounts whenever the body swaps views and a
 *   publish must keep reporting after that.
 * - `detailsFor` — the folder whose details dialog is open. The failure and
 *   warnings toasts' "Details" action sets it, so it works even after the
 *   header that started the publish has remounted.
 */
interface ProBuilderPublishStore {
  seenGitReviews: string[];
  receiveGitReview: (prepared: GitPrepared) => void;
  running: string[];
  setRunning: (folder: string, running: boolean) => void;
  rows: Record<string, PublishRowState>;
  setRow: (folder: string, next: PublishRowState | null) => void;
  detailsFor: string | null;
  openDetails: (folder: string) => void;
  closeDetails: () => void;
}

export const useProBuilderPublishStore = create<ProBuilderPublishStore>((set) => ({
  seenGitReviews: [],
  receiveGitReview: (prepared) => set((s) => s.seenGitReviews.includes(prepared.operationId) ? s : {
    seenGitReviews: [...s.seenGitReviews, prepared.operationId],
    rows: { ...s.rows, [prepared.name]: { phase: 'prepared', prepared } },
    detailsFor: prepared.name,
  }),
  running: [],
  setRunning: (folder, running) =>
    set((s) => {
      const rest = s.running.filter((f) => f !== folder);
      return { running: running ? [...rest, folder] : rest };
    }),
  rows: {},
  setRow: (folder, next) =>
    set((s) => {
      const { [folder]: _gone, ...rest } = s.rows;
      return { rows: next ? { ...rest, [folder]: next } : rest };
    }),
  detailsFor: null,
  openDetails: (folder) => set({ detailsFor: folder }),
  closeDetails: () => set({ detailsFor: null }),
}));
